import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Badge, Button, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import {
  staticColumns,
  CANDIDATE_STATIC_INITIAL_OPTIONS,
  CANDIDATE_STATIC_MANDATORY_OPTIONS,
} from "./CandidateStaticConstants";
import "./CandidateStaticReport.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  DynamicTableHelper,
  useTableHook,
  TooltipWrapper,
} from "@workspace/common";
import {
  fetchCandidateStaticReportCount,
  fetchCandidateStaticReportListing,
} from "../../store/candidate/action";
import DynamicTableWrapper from "./DynamicTableWrapper";

function CandidateStaticReport() {
  document.title = "Overview Static Report | RTS";

  const dispatch = useDispatch();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const candidateStaticReportCount = useSelector(
    (state) => state?.CandidateReducer?.candidateStaticReportCount
  );
  const candidateStaticReportListing = useSelector(
    (state) => state?.CandidateReducer?.candidateStaticReportListing
  );
  const candidateReportFilter = useSelector(
    (state) => state?.CandidateReducer?.candidateStaticReportFilter
  );
  const candidateStaticReportCountMeta = useSelector(
    (state) => state?.CandidateReducer?.candidateStaticReportCountMeta
  );

  // Table State
  const [tableConfig, setTableConfig] = useState(null);
  const [jobStep, setJobStep] = useState("");
  const [isCurrentStage, setIsCurrentStage] = useState(false);

  useEffect(() => {
    if (candidateReportFilter != null) {
      const currentStage = candidateReportFilter.find(
        (filter) => filter.label === "Current Stage"
      );
      if (currentStage?.value) {
        setIsCurrentStage(true);
      } else {
        setIsCurrentStage(false);
      }
    }
  }, [candidateReportFilter]);

  // Custom Renders
  const customRenderList = [
    {
      names: ["job.jobSubmissionData.jobTitle"],
      render: (data) => (
        <div>
          <Link
            className="text-decoration-underline"
            onClick={() => {
              const win = window.open(
                `/jobs/${data?.job?.id}/snapshot`,
                "_blank"
              );
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
          >
            {data?.job?.jobSubmissionData?.jobTitle}
          </Link>
        </div>
      ),
    },
    {
      names: ["job.jobSubmissionData.jobId"],
      render: (data) => (
        <div>
          <Link
            className="text-decoration-underline"
            onClick={() => {
              const win = window.open(
                `/jobs/${data?.job?.id}/snapshot`,
                "_blank"
              );
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
          >
            {data?.job?.jobSubmissionData?.jobId
              ? data?.job?.jobSubmissionData?.jobId
              : "-"}
          </Link>
        </div>
      ),
    },
  ];

  // Calculate Ageing
  const calculateAgeing = (createdAt) => {
    const currentDate = new Date();
    const createdDate = new Date(createdAt);
    const ageingValueMS = currentDate - createdDate;
    const diffDays = Math.floor(ageingValueMS / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const findLatestStep = (obj) => {
    let maxOrder = -Infinity;
    let maxOrderKey = null;

    for (const key in obj) {
      if (obj[key].order > maxOrder) {
        maxOrder = obj[key].order;
        maxOrderKey = key;
      }
    }

    return maxOrderKey;
  };

  const getBadgeColour = (obj) => {
    const rejectedStep = findRejectedStep(obj);
    if (rejectedStep !== null) {
      return "danger";
    } else {
      return "success";
    }
  };

  const findRejectedStep = (obj) => {
    // Each key value is now an array. Flatten the array of object and look for the status
    for (const key in obj) {
      const array = obj[key];
      for (const item of array) {
        if (item.status === "REJECTED" || item.status === "WITHDRAWN") {
          return { [key]: item };
        }
      }
    }
    return null;
  };

  // User Setup
  const generateCandidateStaticReportConfig = (customConfig) => {
    return [
      ...customConfig,
      {
        header: "Candidate Name",
        name: "candidate.candidateSubmissionData.firstName",
        sort: true,
        sortValue: "candidate.candidate_submission_data.firstName",
        expand: true,
        sortType: "jsonb",
        render: (data) => (
          <div>
            <a
              className="text-decoration-underline cursor-pointer"
              onClick={() => {
                const win = window.open(
                  `/candidates/${data?.candidate?.id}/snapshot`,
                  "_blank"
                );
                win.focus();
              }}
              style={{ color: "#0a56ae" }}
            >
              {data?.candidate?.candidateSubmissionData?.firstName}{" "}
              {data?.candidate?.candidateSubmissionData?.lastName}
            </a>
          </div>
        ),
      },
      {
        header: "Current Candidate Status",
        name: "subStepName",
        sort: true,
        sortValue: "sub_step_name",
        expand: true,
        sortType: "normal",
        render: (data) => (
          <Badge color={getBadgeColour(data?.timeline)}>
            {data?.subStepName ? data?.subStepName : "-"}
          </Badge>
        ),
      },
      {
        header: "Ageing",
        name: "job.jobSubmissionData.dateOpen",
        sort: true,
        sortValue: "job.job_submission_data.dateOpen",
        sortType: "jsonb",
        expand: true,
        render: (data) => (
          <div>
            {calculateAgeing(data?.job?.jobSubmissionData?.dateOpen)} Days
          </div>
        ),
      },
    ];
  };

  // Table Hooks
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
    },
    CANDIDATE_STATIC_MANDATORY_OPTIONS,
    CANDIDATE_STATIC_INITIAL_OPTIONS,
    customRenderList,
    generateCandidateStaticReportConfig
  );

  const getCount = (itemKey) => {
    let jobCount = 0;
    switch (itemKey) {
      case "New Requirements":
        jobCount = candidateStaticReportCount.newRequirementsCount ?? 0;
        break;
      case "Active Requirements":
        jobCount = candidateStaticReportCount.activeRequirementsCount ?? 0;
        break;
      case "Associated":
        jobCount = candidateStaticReportCount.associatedCount ?? 0;
        break;
      case "Submitted to Sales":
        jobCount = candidateStaticReportCount.submitToSalesCount ?? 0;
        break;
      case "Submitted to Client":
        jobCount = candidateStaticReportCount.submitToClientCount ?? 0;
        break;
      case "Interview Scheduled":
        if (isCurrentStage) {
          jobCount = candidateStaticReportCount.interviewScheduledCount ?? 0;
        } else {
          const interviewScheduledFirstCount =
            candidateStaticReportCount.interviewScheduledFirstCount ?? 0;
          const interviewScheduledSecondCount =
            candidateStaticReportCount.interviewScheduledSecondCount ?? 0;
          const interviewScheduledThirdCount =
            candidateStaticReportCount.interviewScheduledThirdCount ?? 0;
          jobCount =
            interviewScheduledFirstCount +
            interviewScheduledSecondCount +
            interviewScheduledThirdCount;
        }
        break;
      case "Interview Happened":
        // Check if filter is current Stage == true
        if (isCurrentStage) {
          jobCount = candidateStaticReportCount.interviewHappenedCount ?? 0;
        } else {
          const interviewHappenedFirstCount =
            candidateStaticReportCount?.interviewHappenedFirstCount ?? 0;
          const interviewHappenedSecondCount =
            candidateStaticReportCount?.interviewHappenedSecondCount ?? 0;
          const interviewHappenedThirdCount =
            candidateStaticReportCount?.interviewHappenedThirdCount ?? 0;
          jobCount =
            interviewHappenedFirstCount +
            interviewHappenedSecondCount +
            interviewHappenedThirdCount;
        }
        break;
      case "Selected":
        jobCount = candidateStaticReportCount.selectedCount ?? 0;
        break;
      case "No Submission":
        jobCount = candidateStaticReportCount.noSubmissionsCount ?? 0;
        break;
      case "All Requirements":
        const newCount = candidateStaticReportCount.newRequirementsCount ?? 0;
        const activeCount =
          candidateStaticReportCount.activeRequirementsCount ?? 0;
        jobCount = newCount + activeCount;
        break;
      case "Withdrawn":
        jobCount = candidateStaticReportCount.withdrawnCount ?? 0;
        break;
      default:
        jobCount = 0;
        break;
    }
    return jobCount;
  };

  const hasPageRendered = useRef(false);

  // Get all option groups

  // Fetch the job when the pageRequest changes
  useEffect(() => {
    if (candidateReportFilter != null) {
      dispatch(
        fetchCandidateStaticReportListing(
          DynamicTableHelper.cleanPageRequest({ ...pageRequest, jobStep })
        )
      );
    }
  }, [pageRequest, jobStep]);

  useEffect(() => {
    if (pageRequest?.filters) {
      dispatch(
        fetchCandidateStaticReportCount(
          DynamicTableHelper.cleanPageRequest({ ...pageRequest, jobStep })
        )
      );
    }
  }, [JSON.stringify(pageRequest?.filters)]);

  useEffect(() => {
    if (candidateStaticReportListing) {
      setPageInfoData(candidateStaticReportListing);
      setTableData(candidateStaticReportListing?.jobs);
    }
  }, [candidateStaticReportListing]);

  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateCandidateStaticReportConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        // If cannot find then continue
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateCandidateStaticReportConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  // Check if page has re-rendered
  useEffect(() => {
    if (hasPageRendered.current) {
      pageRequestSet.setPage(0);
    }
    hasPageRendered.current = true;
  }, []);

  const handleSetJobStep = (jobStepName) => {
    if (checkForIsStep(jobStepName)) {
      if (jobStep == "") {
        setJobStep(jobStepName);
      }
      if (jobStep == jobStepName && jobStep !== "") {
        setJobStep("");
      } else {
        setJobStep(jobStepName);
      }
      pageRequestSet.setPage(0);
    }
  };

  const checkForIsStep = (jobStepName) => {
    if (
      jobStepName !== "New Requirements" &&
      jobStepName !== "Active Requirements" &&
      jobStepName !== "All Requirements"
    ) {
      return true;
    }
    return false;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="p-0">
          <Row className="sticky-header sticky-static-candidate-header">
            <Col lg={11}>
              <div className="static-grid">
                {staticColumns.map((item, index) => (
                  <div
                    className={`static-grid-item 
                      ${
                        item.includes("New Requirements") ||
                        item.includes("Active Requirements") ||
                        item.includes("All Requirements")
                          ? ""
                          : item === jobStep
                          ? "static-grid-item-selected"
                          : "static-grid-item-selectable"
                      }
                      `}
                    key={index}
                  >
                    <div
                      className={`d-flex flex-column ${
                        checkForIsStep(item) ? "cursor-pointer" : ""
                      }`}
                      onClick={() => handleSetJobStep(item)}
                    >
                      <span className="static-header-title">{item}</span>
                      <span className="static-header-count">
                        {candidateStaticReportCountMeta?.isLoading ? (
                          <Spinner size="sm" color="primary" />
                        ) : (
                          getCount(item)
                        )}
                      </span>
                    </div>
                    {jobStep === item && (
                      <span className="mdi mdi-check-circle-outline static-grid-item-selected-icon"></span>
                    )}
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={1} className="text-end m-0 p-0">
              <TooltipWrapper tooltipText="Filter">
                <Button
                  hidden={false}
                  className="bg-white main-border-style"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <span className="mdi mdi-filter-variant align-bottom"></span>
                </Button>
              </TooltipWrapper>
            </Col>
          </Row>

          <div className="pt-2 px-2">
            {/* Table */}
            <DynamicTableWrapper
              data={candidateStaticReportListing?.jobs ?? []}
              config={tableConfig}
              pageInfo={pageInfo}
              pageRequest={pageRequest}
              pageRequestSet={pageRequestSet}
              search={search}
              setSearch={setSearch}
              // optGroup={candidatesFields}
              setCustomConfigData={setCustomConfigData}
              header="Candidates Static Report"
              activeRow={activeRow}
              setActiveRow={setActiveRow}
              setTableConfig={setTableConfig}
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CandidateStaticReport;
